import JSZip from 'jszip';

const RES_STRING_POOL_TYPE = 0x0001;
const RES_XML_TYPE = 0x0003;
const RES_XML_START_ELEMENT_TYPE = 0x0102;
const NO_INDEX = 0xffffffff;
const UTF8_FLAG = 0x00000100;

const TYPE_STRING = 0x03;
const TYPE_INT_DEC = 0x10;
const TYPE_INT_HEX = 0x11;

export interface ApkManifestVersionInfo {
  packageName?: string;
  versionName?: string;
  versionCode?: number;
}

class LittleEndianReader {
  private readonly view: DataView;
  private readonly bytes: Uint8Array;

  constructor(bytes: Uint8Array) {
    this.bytes = bytes;
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }

  get length() {
    return this.bytes.length;
  }

  u8(offset: number): number {
    this.assertRange(offset, 1);
    return this.view.getUint8(offset);
  }

  u16(offset: number): number {
    this.assertRange(offset, 2);
    return this.view.getUint16(offset, true);
  }

  u32(offset: number): number {
    this.assertRange(offset, 4);
    return this.view.getUint32(offset, true);
  }

  slice(start: number, end: number): Uint8Array {
    if (start < 0 || end < start || end > this.bytes.length) {
      throw new Error('APK 数据越界');
    }
    return this.bytes.subarray(start, end);
  }

  private assertRange(offset: number, length: number): void {
    if (offset < 0 || offset + length > this.bytes.length) {
      throw new Error('APK 数据格式错误');
    }
  }
}

const decodeUtf8Length = (reader: LittleEndianReader, offset: number): { value: number; next: number } => {
  const first = reader.u8(offset);
  if ((first & 0x80) === 0) {
    return { value: first, next: offset + 1 };
  }
  const second = reader.u8(offset + 1);
  return { value: ((first & 0x7f) << 8) | second, next: offset + 2 };
};

const decodeUtf16Length = (reader: LittleEndianReader, offset: number): { value: number; next: number } => {
  const first = reader.u16(offset);
  if ((first & 0x8000) === 0) {
    return { value: first, next: offset + 2 };
  }
  const second = reader.u16(offset + 2);
  return { value: ((first & 0x7fff) << 16) | second, next: offset + 4 };
};

const decodeUtf8String = (reader: LittleEndianReader, offset: number): string => {
  const firstLength = decodeUtf8Length(reader, offset);
  const secondLength = decodeUtf8Length(reader, firstLength.next);
  const valueStart = secondLength.next;
  const valueEnd = valueStart + secondLength.value;
  const bytes = reader.slice(valueStart, valueEnd);
  return new TextDecoder('utf-8').decode(bytes);
};

const decodeUtf16String = (reader: LittleEndianReader, offset: number): string => {
  const decodedLength = decodeUtf16Length(reader, offset);
  let cursor = decodedLength.next;
  const chars: string[] = [];
  for (let i = 0; i < decodedLength.value; i += 1) {
    chars.push(String.fromCharCode(reader.u16(cursor)));
    cursor += 2;
  }
  return chars.join('');
};

const parseStringPool = (reader: LittleEndianReader, chunkOffset: number): string[] => {
  const headerSize = reader.u16(chunkOffset + 2);
  const chunkSize = reader.u32(chunkOffset + 4);
  const stringCount = reader.u32(chunkOffset + 8);
  const flags = reader.u32(chunkOffset + 16);
  const stringsStart = reader.u32(chunkOffset + 20);
  const isUtf8 = (flags & UTF8_FLAG) !== 0;

  const offsetsStart = chunkOffset + headerSize;
  const stringsBase = chunkOffset + stringsStart;
  const strings: string[] = [];

  for (let i = 0; i < stringCount; i += 1) {
    const relativeOffset = reader.u32(offsetsStart + i * 4);
    const absoluteOffset = stringsBase + relativeOffset;
    if (absoluteOffset >= chunkOffset + chunkSize) {
      strings.push('');
      continue;
    }
    const value = isUtf8
      ? decodeUtf8String(reader, absoluteOffset)
      : decodeUtf16String(reader, absoluteOffset);
    strings.push(value);
  }

  return strings;
};

const toStringValue = (strings: string[], index: number): string => {
  if (index === NO_INDEX || index < 0 || index >= strings.length) {
    return '';
  }
  return strings[index] || '';
};

const parseManifestAttributes = (
  reader: LittleEndianReader,
  chunkOffset: number,
  headerSize: number,
  chunkSize: number,
  strings: string[]
): ApkManifestVersionInfo => {
  const elementName = toStringValue(strings, reader.u32(chunkOffset + 20));
  if (elementName !== 'manifest') {
    return {};
  }

  const attributeStart = reader.u16(chunkOffset + 24);
  const attributeSize = reader.u16(chunkOffset + 26);
  const attributeCount = reader.u16(chunkOffset + 28);
  let attributesOffset = chunkOffset + 16 + attributeStart;
  if (attributesOffset < chunkOffset || attributesOffset >= chunkOffset + chunkSize) {
    attributesOffset = chunkOffset + headerSize;
  }

  const result: ApkManifestVersionInfo = {};

  for (let i = 0; i < attributeCount; i += 1) {
    const currentOffset = attributesOffset + i * attributeSize;
    if (currentOffset + 20 > chunkOffset + chunkSize) {
      break;
    }

    const name = toStringValue(strings, reader.u32(currentOffset + 4));
    const rawValueIndex = reader.u32(currentOffset + 8);
    const valueType = reader.u8(currentOffset + 15);
    const valueData = reader.u32(currentOffset + 16);

    const rawValue = toStringValue(strings, rawValueIndex);
    const stringValue = rawValue || (valueType === TYPE_STRING ? toStringValue(strings, valueData) : '');

    if (name === 'package') {
      result.packageName = stringValue;
      continue;
    }

    if (name === 'versionName') {
      result.versionName = stringValue;
      continue;
    }

    if (name !== 'versionCode') {
      continue;
    }

    if (valueType === TYPE_INT_DEC || valueType === TYPE_INT_HEX) {
      result.versionCode = valueData;
      continue;
    }

    const parsed = Number.parseInt(stringValue, 10);
    if (Number.isFinite(parsed)) {
      result.versionCode = parsed;
    }
  }

  return result;
};

const parseAndroidManifestBinaryXml = (manifestBytes: Uint8Array): ApkManifestVersionInfo => {
  const reader = new LittleEndianReader(manifestBytes);
  const strings: string[] = [];
  let cursor = 0;

  while (cursor + 8 <= reader.length) {
    const chunkType = reader.u16(cursor);
    const headerSize = reader.u16(cursor + 2);
    const chunkSize = reader.u32(cursor + 4);

    if (chunkSize < headerSize || cursor + chunkSize > reader.length) {
      throw new Error('AndroidManifest.xml 格式无效');
    }

    if (chunkType === RES_XML_TYPE && cursor === 0) {
      cursor += headerSize;
      continue;
    }

    if (chunkType === RES_STRING_POOL_TYPE) {
      const parsed = parseStringPool(reader, cursor);
      strings.splice(0, strings.length, ...parsed);
      cursor += chunkSize;
      continue;
    }

    if (chunkType === RES_XML_START_ELEMENT_TYPE && strings.length > 0) {
      const result = parseManifestAttributes(reader, cursor, headerSize, chunkSize, strings);
      if (result.versionName || result.versionCode) {
        return result;
      }
    }

    cursor += chunkSize;
  }

  return {};
};

export const parseApkManifestVersionInfo = async (file: File): Promise<ApkManifestVersionInfo> => {
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const manifestFile = zip.file('AndroidManifest.xml');
  if (!manifestFile) {
    throw new Error('安装包中缺少 AndroidManifest.xml');
  }

  const manifestBytes = await manifestFile.async('uint8array');
  return parseAndroidManifestBinaryXml(manifestBytes);
};
